import { __assign, __read, __rest } from "tslib";
import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import { useDefault } from "../_util/use-default";
import { Icon } from "../icon";
import { FadeTransition } from "../transition";
import { useConfig } from "../_util/config-context";
import { Button } from "../button";
import { AlertNotice } from "./AlertNotice";
import { useTranslation } from "../i18n";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { callBoth } from "../util";
import { uuid } from "../_util/uuid";
var iconMap = {
    info: "infoblue",
    success: "success",
    warning: "warning",
    error: "error",
};
export var Alert = forwardRefWithStatics(function Alert(_a, ref) {
    var _b;
    var type = _a.type, children = _a.children, className = _a.className, onClose = _a.onClose, extra = _a.extra, hideIcon = _a.hideIcon, carouselMode = _a.carouselMode, _c = _a.interval, interval = _c === void 0 ? 5000 : _c, _visible = _a.visible, _defaultVisible = _a.defaultVisible, props = __rest(_a, ["type", "children", "className", "onClose", "extra", "hideIcon", "carouselMode", "interval", "visible", "defaultVisible"]);
    var classPrefix = useConfig().classPrefix;
    var t = useTranslation();
    var _d = __read(useDefault(_visible, _defaultVisible, function (visible) { return !visible && onClose && onClose(); }), 2), visible = _d[0], onVisibleChange = _d[1];
    var isClosable = typeof visible === "boolean";
    var timerRef = useRef(null);
    var _e = __read(useState(0), 2), currentIndex = _e[0], setCurrentIndex = _e[1];
    var length = React.Children.toArray(children).filter(Boolean).length;
    var circulate = useCallback(function () {
        if (!carouselMode) {
            return;
        }
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(function () {
            setCurrentIndex(function (i) { return (i + 1) % length; });
            circulate();
        }, interval);
    }, [carouselMode, interval, length]);
    useEffect(function () {
        circulate();
        return function () { return clearTimeout(timerRef.current); };
    }, [circulate]);
    var _f = __read(useState(uuid()), 2), id = _f[0], setId = _f[1];
    useEffect(function () {
        setId(uuid());
    }, [children]);
    var alertClassName = classNames(classPrefix + "-alert", (_b = {},
        _b[classPrefix + "-alert--" + type] = type,
        _b), className);
    var alertElement = function (children) {
        var _a, _b;
        return (React.createElement("div", __assign({ ref: ref, className: alertClassName }, props, { onMouseEnter: callBoth(function () {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
            }, (_a = props) === null || _a === void 0 ? void 0 : _a.onMouseEnter), onMouseLeave: callBoth(circulate, (_b = props) === null || _b === void 0 ? void 0 : _b.onMouseLeave) }),
            !hideIcon && (React.createElement("span", { className: classPrefix + "-alert__decoration" },
                React.createElement(Icon, { type: iconMap[type] || "infoblue" }))),
            React.createElement("div", { className: classPrefix + "-alert__info" }, children),
            React.createElement("div", { className: classPrefix + "-alert__operational" },
                extra,
                carouselMode && (React.createElement(Dot, { classPrefix: classPrefix, total: length, currentIndex: currentIndex, onChange: function (index) {
                        setCurrentIndex(index);
                        circulate();
                    } })),
                isClosable && (React.createElement(Button, { type: "icon", icon: "close", htmlType: "button", onClick: function () { return onVisibleChange(false); }, "aria-label": t.closeText })))));
    };
    var content = !carouselMode
        ? alertElement(children)
        : alertElement(React.Children.toArray(children)
            .filter(Boolean)
            .map(function (c, i) { return (React.createElement("div", { key: id + "-" + i, style: i === currentIndex ? undefined : { display: "none" } }, c)); }));
    if (isClosable) {
        return React.createElement(FadeTransition, { in: visible }, content);
    }
    return content;
}, {
    Notice: AlertNotice,
});
Alert.displayName = "Alert";
function Dot(_a) {
    var classPrefix = _a.classPrefix, total = _a.total, currentIndex = _a.currentIndex, onChange = _a.onChange;
    return (React.createElement("div", { className: classPrefix + "-alert__nav " + classPrefix + "-alert__nav--dot" }, Array(total)
        .fill(null)
        .map(function (_, i) { return (React.createElement("span", { key: i, className: classNames(classPrefix + "-alert__nav-item", {
            "is-current": currentIndex === i,
        }), onClick: function () { return onChange(i); } })); })));
}
//# sourceMappingURL=Alert.js.map